import { useQuery } from '@tanstack/react-query';
import { adminApi, type StudentTeacherControlBreakdown } from '../../../api/adminApi';

interface TeacherControlsTabProps {
  userId: string;
}

function BoolBadge({ value }: { value: boolean }) {
  return (
    <span style={{
      padding: '2px 8px',
      borderRadius: 10,
      fontSize: 11,
      fontWeight: 600,
      background: value ? 'var(--status-success-bg, #dcfce7)' : 'var(--status-error-bg, #fee2e2)',
      color: value ? 'var(--status-success-text, #16a34a)' : 'var(--status-error-text, #dc2626)',
    }}>
      {value ? 'Enabled' : 'Disabled'}
    </span>
  );
}

export function TeacherControlsTab({ userId }: TeacherControlsTabProps) {
  const { data: teacherControlsData, isLoading: controlsLoading } = useQuery({
    queryKey: ['studentTeacherControls', userId],
    queryFn: () => adminApi.getStudentTeacherControls(userId),
    enabled: !!userId,
  });

  return (
    <div className="features-section">
      {controlsLoading ? (
        <div className="loading">Loading teacher controls...</div>
      ) : !teacherControlsData || teacherControlsData.controls.length === 0 ? (
        <p className="no-features-message">No teacher controls found. This student may not be enrolled with any teacher.</p>
      ) : (
        <table className="dt dt--compact" style={{ fontSize: 13 }}>
          <thead>
            <tr>
              <th style={{ padding: '8px 10px' }}>Subject</th>
              <th style={{ padding: '8px 10px' }}>Control</th>
              <th style={{ padding: '8px 10px' }}>System Default</th>
              <th style={{ padding: '8px 10px' }}>Teacher Default</th>
              <th style={{ padding: '8px 10px' }}>Student Override</th>
              <th style={{ padding: '8px 10px' }}>Resolved</th>
              <th style={{ padding: '8px 10px' }}>Teacher</th>
            </tr>
          </thead>
          <tbody>
            {teacherControlsData.controls.map((c: StudentTeacherControlBreakdown, i: number) => (
              <tr key={i}>
                <td style={{ padding: '8px 10px', textTransform: 'capitalize' }}>{c.subject}</td>
                <td style={{ padding: '8px 10px' }}>
                  {c.controlName === 'showHints' ? 'Show Hints' : 'Allow Pre-Submit Analysis'}
                </td>
                <td style={{ padding: '8px 10px' }}>
                  <BoolBadge value={c.systemDefault} />
                </td>
                <td style={{ padding: '8px 10px' }}>
                  {c.teacherDefault !== undefined ? <BoolBadge value={c.teacherDefault} /> : <span style={{ color: 'var(--text-tertiary)' }}>-</span>}
                </td>
                <td style={{ padding: '8px 10px' }}>
                  {c.studentOverride !== undefined ? <BoolBadge value={c.studentOverride} /> : <span style={{ color: 'var(--text-tertiary)' }}>-</span>}
                </td>
                <td style={{ padding: '8px 10px' }}>
                  <BoolBadge value={c.resolved} />
                </td>
                <td style={{ padding: '8px 10px' }}>
                  {c.teacherName || c.teacherId.substring(0, 8)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
